import React, { useRef, useState } from 'react';
import './AudienceCarousel.css';
import audienceData from './audienceCarouselData.json';
import { bgImageSet } from './bgImageSet';

import conversionPowerImg from 'url:../../assets-optimized/redesign/ourPlatform/conversion_power.jpg';
import conversionPowerWebp from 'url:../../assets-optimized/redesign/ourPlatform/conversion_power.webp';
import conversionPowerAvif from 'url:../../assets-optimized/redesign/ourPlatform/conversion_power.avif';
import travelSignalImg from 'url:../../assets-optimized/redesign/ourPlatform/travel_is_the_signal.png';
import travelSignalWebp from 'url:../../assets-optimized/redesign/ourPlatform/travel_is_the_signal.webp';
import travelSignalAvif from 'url:../../assets-optimized/redesign/ourPlatform/travel_is_the_signal.avif';
import firstPartyDataImg from 'url:../../assets-optimized/redesign/ourPlatform/first_party_data.jpg';
import firstPartyDataWebp from 'url:../../assets-optimized/redesign/ourPlatform/first_party_data.webp';
import firstPartyDataAvif from 'url:../../assets-optimized/redesign/ourPlatform/first_party_data.avif';

const imageMap = {
  'conversion_power.jpg': bgImageSet({
    avif: conversionPowerAvif,
    webp: conversionPowerWebp,
    fallback: conversionPowerImg,
  }),
  'travel_is_the_signal.png': bgImageSet({
    avif: travelSignalAvif,
    webp: travelSignalWebp,
    fallback: travelSignalImg,
  }),
  'first_party_data.jpg': bgImageSet({
    avif: firstPartyDataAvif,
    webp: firstPartyDataWebp,
    fallback: firstPartyDataImg,
  }),
};

const ArrowIcon = ({ dir }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='white'
    strokeWidth='1.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    {dir === 'prev' ? (
      <>
        <line x1='19' y1='12' x2='5' y2='12' />
        <polyline points='12 19 5 12 12 5' />
      </>
    ) : (
      <>
        <line x1='5' y1='12' x2='19' y2='12' />
        <polyline points='12 5 19 12 12 19' />
      </>
    )}
  </svg>
);

const AudienceCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  // The slide currently sliding away on top, plus the direction it travels.
  const [leaving, setLeaving] = useState(null); // { index, dir: 'next' | 'prev' }
  const activeSlide = audienceData[activeIndex];

  const handlePrev = () => {
    setLeaving({ index: activeIndex, dir: 'prev' });
    setActiveIndex((prev) => (prev === 0 ? audienceData.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setLeaving({ index: activeIndex, dir: 'next' });
    setActiveIndex((prev) => (prev === audienceData.length - 1 ? 0 : prev + 1));
  };

  const handleBgAnimationEnd = (idx) => {
    setLeaving((cur) => (cur && cur.index === idx ? null : cur));
  };

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    // Only treat as a horizontal swipe if it's clearly horizontal and far enough.
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <section
      id='audience-carousel'
      className='audience-carousel-section'
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {audienceData.map((slide, idx) => {
        const isLeaving = leaving && leaving.index === idx;
        const leavingClass = isLeaving
          ? leaving.dir === 'next'
            ? 'leaving-next'
            : 'leaving-prev'
          : '';
        return (
          <div
            key={slide.id}
            className={`audience-carousel-bg ${idx === activeIndex ? 'active' : ''} ${leavingClass}`}
            style={{ backgroundImage: imageMap[slide.image] }}
            onAnimationEnd={() => handleBgAnimationEnd(idx)}
            aria-hidden='true'
          />
        );
      })}

      <div className='sr-only' aria-live='polite'>
        {`Slide ${activeIndex + 1} of ${audienceData.length}: ${activeSlide.title}`}
      </div>

      <div className='ac-overlay'>
        <div className='ac-top' key={activeSlide.id}>
          <h2 className='ac-title'>{activeSlide.title}</h2>
          {activeSlide.subtitle && <span className='ac-subtitle'>{activeSlide.subtitle}</span>}
        </div>

        <button
          className='audience-nav-arrow audience-nav-prev'
          onClick={handlePrev}
          aria-label='Previous slide'
        >
          <ArrowIcon dir='prev' />
        </button>
        <button
          className='audience-nav-arrow audience-nav-next'
          onClick={handleNext}
          aria-label='Next slide'
        >
          <ArrowIcon dir='next' />
        </button>

        <div className='ac-bottom'>
          <div className='ac-bottom-bar' key={`bar-${activeSlide.id}`}>
            <p className='ac-bottom-text'>{activeSlide.body}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceCarousel;
